import { GetServerSideProps, NextComponentType } from 'next'
import { Document } from '@contentful/rich-text-types'
import { useMemo, useState } from 'react'
import { Options, documentToHtmlString } from '@contentful/rich-text-html-renderer'
import Head from 'next/head'
import StaticPageContent from 'components/StaticPageContent'
import { CustomTabsType, ThemeEnum } from 'interfaces'
import HeadingContent from 'components/HeadingContent/HeadingContent'
import { getCMSPageContents } from 'api/CMS.api'
import { FlexBox, trackEvent } from '@rario/shared-components'
import { getFormattedEventName } from 'utils/utils'
import { PLATFORM_ORIGIN } from 'config'

const CustomPage: NextComponentType<
  {},
  {},
  { pageContent: CustomTabsType[]; title: string; platform?: string }
> = ({ pageContent, title, platform }) => {
  let initialTab = pageContent[0]
  if (platform === 'D3') {
    initialTab = pageContent[1]
  }

  const [selectedDocument] = useState(initialTab.value)
  const eventName = getFormattedEventName(title)

  const htmlContent = useMemo(() => {
    const options: Options = {
      renderNode: {
        paragraph: (node, next) => `<p>${next(node.content).replace(/\n/g, `</br>`)}</p>`,
      },
    }
    trackEvent('Section Viewed', 'track', {
      sub_category: `policies-${eventName}`,
      category: 'policies',
      type: 'page_view',
      policy_platform: platform || 'Rario',
    })
    return documentToHtmlString(selectedDocument as Document, options)
  }, [documentToHtmlString, pageContent, selectedDocument])

  return (
    <>
      <Head>
        <title>{title} | Rario</title>
      </Head>
      <FlexBox ml="10px">
        <HeadingContent heading={title} markerLeftPosition="-10px" />
      </FlexBox>
      <StaticPageContent htmlContent={htmlContent} pageContent={selectedDocument} />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query, res }) => {
  const { cmsPageId, platform } = query

  try {
    const { data } = await getCMSPageContents(cmsPageId as string)

    if (!data) {
      return {
        redirect: {
          permanent: false,
          destination: '/404',
        },
        props: {},
      }
    }

    const { html, crhtml, d3html, addfund, withdrawal, title } = data
    res.setHeader('Cache-Control', 'max-age=0, s-maxage=900')
    return {
      props: {
        pageContent: [
          {
            label: PLATFORM_ORIGIN === ThemeEnum.RARIO ? 'Rario App' : 'CricRush',
            value: html ?? null,
          },
          { label: 'D3 Game', value: d3html ?? null },
          { label: 'Wallet', value: addfund ?? null },
          { label: 'Australia', value: crhtml ?? null },
          { label: 'Withdrawal', value: withdrawal ?? null },
        ],
        title,
        platform: platform ?? null,
      },
    }
  } catch (e) {
    console.log('csutom cms page error: ', e)
    return {
      redirect: {
        permanent: false,
        destination: '/404',
      },
      props: {},
    }
  }
}

export default CustomPage
